import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link, useLocation } from "react-router-dom";
import api, { fileUrl, formatApiError, formatBRL } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useAuthModal } from "../lib/authModal";
import { toast } from "sonner";
import { Camera, Trash, ArrowRight, Truck, House, Package, CheckCircle, ArrowLeft, X, Plus, MapPin, Tag, Clock, Spinner, CaretDown } from "@phosphor-icons/react";
import { PricingRulesEditor, sanitizePricingRules } from "../components/PricingRulesEditor";
import { AdvancedListingConfig } from "../components/AdvancedListingConfig";
import ConfirmDialog from "../components/ConfirmDialog";
import { processAndUploadImage, processAndUploadImagePair } from "../lib/imageUpload";
import { isNative } from "../lib/native";
import { PhotoSourceSheet } from "../components/PhotoSourceSheet";
import { LISTING_FORM } from "../constants/testIds";

const ENTREGA_OPTIONS = [
  { val: "eu_entrego", label: "Eu entrego", icon: Truck },
  { val: "locatario_busca", label: "Locatário busca", icon: House },
  { val: "ambos", label: "Ambos", icon: Package },
];

// V7.7: botão de upload com inputRef.click() — padrão mais robusto pra mobile/Capacitor
// (substitui o padrão <label><input hidden/></label> que falha em alguns webviews iOS/Android)
function UploadImageButton({ onChange, onCaptureMultiple, uploading, remaining = 10 }) {
  const inputRef = useRef(null);
  const [showSheet, setShowSheet] = useState(false);
  const handleClick = () => {
    if (uploading) return;
    if (isNative()) { setShowSheet(true); return; }  // nativo: menu Câmera / Galeria
    inputRef.current?.click();
  };
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={onChange}
        style={{ display: "none" }}
        data-testid="upload-image-input"
      />
      <button
        type="button"
        onClick={handleClick}
        disabled={uploading}
        className="aspect-square rounded-xl border-2 border-dashed border-slate-300 flex flex-col items-center justify-center hover:bg-slate-50 active:bg-slate-100 transition disabled:opacity-50"
        data-testid={LISTING_FORM.uploadImageBtn}
      >
        <Camera size={22} weight="bold" className="text-[#004E98]" />
        <span className="text-[11px] font-bold mt-1">{uploading ? "Subindo..." : "Adicionar"}</span>
      </button>
      <PhotoSourceSheet
        open={showSheet}
        onClose={() => setShowSheet(false)}
        onPick={(files) => onCaptureMultiple?.(files)}
        multiple
        limit={Math.max(1, remaining)}
      />
    </>
  );
}

// V18.1 — Tile placeholder com indicador circular de progresso enquanto a foto
// é comprimida/enviada. Fica no lugar onde a foto vai aparecer, com a prévia
// local ao fundo (blur suave) e um donut SVG por cima mostrando o %.
function UploadProgressTile({ task, onRetry, onDismiss }) {
  const R = 22;            // raio do círculo (viewBox 60)
  const C = 2 * Math.PI * R;
  const pct = Math.max(0, Math.min(100, task.progress || 0));
  const strokeOffset = C * (1 - pct / 100);
  const isError = task.phase === "error";
  const isDone = task.phase === "done";
  const label = isError ? "Falhou"
    : isDone ? "OK"
    : task.phase === "compressing" ? "Preparando"
    : "Enviando";
  return (
    <div
      className={`relative aspect-square rounded-xl overflow-hidden border-2 ${isError ? "border-red-300 bg-red-50" : "border-[#004E98]/40 bg-slate-100"}`}
      data-testid={`upload-tile-${task.id}`}
    >
      {task.previewUrl && !isError ? (
        <img src={task.previewUrl} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40 blur-[1px]" />
      ) : null}
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 backdrop-blur-[1px]">
        {isError ? (
          <>
            <span className="text-red-700 text-[10px] font-black text-center px-1" data-testid={`upload-tile-error-${task.id}`}>
              Falhou
            </span>
            <div className="flex items-center gap-1 mt-1">
              <button type="button" onClick={onRetry} className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-white border border-red-300 text-red-700 hover:bg-red-50" data-testid={`upload-tile-retry-${task.id}`}>
                Tentar
              </button>
              <button type="button" onClick={onDismiss} className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-white border border-slate-300 text-slate-600 hover:bg-slate-50" data-testid={`upload-tile-dismiss-${task.id}`}>
                Remover
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="relative">
              <svg width="60" height="60" viewBox="0 0 60 60" className="-rotate-90">
                <circle cx="30" cy="30" r={R} fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="5" />
                <circle
                  cx="30" cy="30" r={R} fill="none"
                  stroke={isDone ? "#059669" : "#004E98"}
                  strokeWidth="5"
                  strokeLinecap="round"
                  strokeDasharray={C}
                  strokeDashoffset={strokeOffset}
                  style={{ transition: "stroke-dashoffset 220ms ease, stroke 220ms ease" }}
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className={`text-[11px] font-black ${isDone ? "text-emerald-700" : "text-[#004E98]"}`} data-testid={`upload-tile-pct-${task.id}`}>
                  {isDone ? "✓" : `${pct}%`}
                </span>
              </div>
            </div>
            <span className="text-[10px] font-bold text-slate-700 tracking-wide" data-testid={`upload-tile-phase-${task.id}`}>
              {label}
            </span>
          </>
        )}
      </div>
    </div>
  );
}


const DRAFT_KEY = "aj_listing_form_draft";

// V13.x: resumo fixo (foto + nome) durante o cadastro — lembra o que está sendo anunciado
function ListingMiniSummary({ image, title }) {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { setLoaded(false); }, [image]);
  return (
    <div className="sticky top-2 z-20 mb-4 flex items-center gap-3 rounded-xl border border-slate-200 bg-white/90 backdrop-blur px-2.5 py-2 shadow-sm" data-testid="listing-mini-summary">
      <div className="w-14 h-14 rounded-lg overflow-hidden bg-slate-100 shrink-0 relative">
        {image ? (
          <>
            {!loaded && (
              <div className="absolute inset-0 flex items-center justify-center" data-testid="mini-summary-loading">
                <Spinner size={16} className="animate-spin text-slate-400" />
              </div>
            )}
            <img
              src={fileUrl(image)}
              alt=""
              onLoad={() => setLoaded(true)}
              className={`w-full h-full object-cover transition-opacity duration-300 ${loaded ? "opacity-100" : "opacity-0"}`}
              data-testid="mini-summary-image"
            />
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Camera size={18} className="text-slate-300" weight="bold" />
          </div>
        )}
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Você está cadastrando</div>
        <div className={`text-sm font-bold truncate ${title?.trim() ? "text-slate-800" : "text-slate-400 italic"}`} data-testid="mini-summary-title">
          {title?.trim() || "Sem título ainda"}
        </div>
      </div>
    </div>
  );
}

const EMPTY_FORM = {
  images: [],
  thumbnails: [],
  title: "",
  description: "",
  category: "",
  categories: [],
  price_per_day: "",
  regras_entrega: "eu_entrego",
  valor_frete: "",
  // Epic 2: config de frete dinâmico
  frete_tipo: "fixo",            // gratis | fixo | por_km | combinar
  frete_raio_gratis_km: "",
  frete_valor_fixo: "",
  frete_valor_por_km: "",
  frete_ida_volta: false,
  // V18.7 — Cutoffs por anúncio (opcional; se vazio usa config global do app)
  cutoff_retirada: "",
  cutoff_entrega: "",
  address_id: "",
  tags: [],
  // V7.2
  quantidade_total: 1,
  tipo_cobranca: "DIARIO",
  // Epic 1: modalidade de locação (padrão | flexible | closed)
  rental_mode: "legacy",
  cycle_unit: "month",
  min_cycles: 1,
  // V12.4: descontos progressivos + promoção de fim de semana
  discount_rules: [],
  weekend_promo: { active: false, type: "DISCOUNT_PERCENT", discount_pct: "", pickup_from: "12:00", return_until: "12:00" },
  // V18.9: descontos progressivos por QUANTIDADE de itens
  quantity_discount_rules: [],
  // V17: cadastro avançado — quantidade, complementos, caução e combos
  qty_min: "",
  qty_step: "",
  conv_active: false,
  conv_question: "",
  conv_unit: "",
  conv_factor: "",
  conv_required: false,
  complements: [],
  caucao_active: false,
  caucao_type: "fixed",
  caucao_value: "",
  caucao_accept: false,
  combos: [],
};

function readDraft(isEdit) {
  if (isEdit) return null;
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

export default function ListingForm() {
  const nav = useNavigate();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const routerLocation = useLocation();
  const isEdit = Boolean(id);
  const { user } = useAuth();
  const { setOpen, requireAuth } = useAuthModal();
  // Lazy initializer hydrates synchronously from sessionStorage (avoids effect race)
  const [step, setStep] = useState(() => {
    const d = readDraft(isEdit);
    return d?.step || 1;
  });
  const [categoriesAll, setCategoriesAll] = useState([]);
  const [catSearch, setCatSearch] = useState("");
  const [addresses, setAddresses] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [form, setForm] = useState(() => {
    const d = readDraft(isEdit);
    const draftForm = d?.form || {};
    return { ...EMPTY_FORM, ...draftForm };
  });
  const [uploading, setUploading] = useState(false);
  // V18.1 — Tasks de upload em andamento (uma por arquivo) para mostrar progress circular
  // no card placeholder enquanto a foto processa/envia.
  const [uploadTasks, setUploadTasks] = useState([]);
  const [saving, setSaving] = useState(false);
  const [pixValidated, setPixValidated] = useState(null); // null=loading, true/false
  const [ownListings, setOwnListings] = useState([]); // V17: p/ combos
  // V17.9: mostra modalidades avançadas (mínimo flexível + prazo fechado) só se o usuário abrir
  const [showAdvancedRentalModes, setShowAdvancedRentalModes] = useState(false);
  // V17.9.2: modal de descartar rascunho (Capacitor não suporta window.confirm de forma confiável)
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get(`/users/${user.id}/listings`, { params: { exclude: id || undefined } })
      .then((r) => setOwnListings(r.data || []))
      .catch(() => {});
  }, [user, id]);

  // V17.9: se o item já usa flexible/closed, abre a seção avançada por padrão
  useEffect(() => {
    if (form.rental_mode === "flexible" || form.rental_mode === "closed") {
      setShowAdvancedRentalModes(true);
    }
  }, [form.rental_mode]);

  // V9.0: anunciar exige chave PIX cadastrada e validada
  useEffect(() => {
    if (!user || user.status !== "ativo_verificado") return;
    let active = true;
    (async () => {
      try {
        const { data } = await api.get("/wallet/pix-key");
        if (active) setPixValidated(!!data.validated);
      } catch {
        if (active) setPixValidated(false);
      }
    })();
    return () => { active = false; };
  }, [user]);

  // Persist on change (safe because state was hydrated synchronously above)
  useEffect(() => {
    if (isEdit) return;
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ form, step }));
  }, [form, step, isEdit]);

  useEffect(() => {
    api.get("/categories").then((r) => {
      setCategoriesAll(r.data.all);
    });
    if (user) {
      // V18.7.2 — Auto-preenche cutoff_retirada com o horário final do expediente do dono (uma vez)
      api.get("/me/atendimento").then((r) => {
        const dias = r?.data?.dias || {};
        // Pega o MAIOR horário final entre os dias abertos
        let maxEnd = "";
        Object.values(dias).forEach((d) => {
          (d?.intervalos || []).forEach((iv) => {
            if (iv?.fecha && iv.fecha > maxEnd) maxEnd = iv.fecha;
          });
        });
        if (maxEnd) {
          setForm((f) => f.cutoff_retirada ? f : { ...f, cutoff_retirada: maxEnd });
        }
      }).catch(() => { /* sem expediente cadastrado — mantém vazio (usa global) */ });
      api.get("/addresses").then((r) => {
        setAddresses(r.data);
        // V7.1: prefere o ?selectedAddressId vindo do retorno de /enderecos
        const selectedFromQuery = searchParams.get("selectedAddressId");
        const def = r.data.find((a) => a.is_default);
        if (selectedFromQuery && r.data.some((a) => a.id === selectedFromQuery)) {
          setForm((f) => ({ ...f, address_id: selectedFromQuery }));
          // Limpa o query param após uso
          const next = new URLSearchParams(searchParams);
          next.delete("selectedAddressId");
          setSearchParams(next, { replace: true });
          toast.success("Endereço selecionado!");
        } else if (def && !isEdit) {
          setForm((f) => ({ ...f, address_id: def.id }));
        }
      });
    }
    if (!user) requireAuth(() => {});
    if (isEdit) {
      api.get(`/listings/${id}`).then((r) => {
        const it = r.data;
        setForm({
          images: it.images || [],
          thumbnails: it.thumbnails || [],
          title: it.title || "",
          description: it.description || "",
          category: it.category || "",
          categories: Array.isArray(it.categories) && it.categories.length ? it.categories : (it.category ? [it.category] : []),
          price_per_day: String(it.price_per_day || ""),
          regras_entrega: it.regras_entrega || "eu_entrego",
          valor_frete: it.valor_frete ? String(it.valor_frete) : "",
          frete_tipo: it.frete_config?.tipo || "fixo",
          frete_raio_gratis_km: it.frete_config?.raio_gratis_km ? String(it.frete_config.raio_gratis_km) : "",
          frete_valor_fixo: it.frete_config?.valor_fixo ? String(it.frete_config.valor_fixo) : (it.valor_frete ? String(it.valor_frete) : ""),
          frete_valor_por_km: it.frete_config?.valor_por_km ? String(it.frete_config.valor_por_km) : "",
          frete_ida_volta: !!it.frete_config?.ida_volta,
          cutoff_retirada: it.cutoff_retirada || "",
          cutoff_entrega: it.cutoff_entrega || "14:00",
          address_id: it.address_id || "",
          tags: it.tags || [],
          quantidade_total: it.quantidade_total || 1,
          tipo_cobranca: it.tipo_cobranca || "DIARIO",
          rental_mode: it.rental_mode || "legacy",
          cycle_unit: it.cycle_unit || (it.tipo_cobranca === "MENSAL" ? "month" : "day"),
          min_cycles: it.min_cycles || 1,
          discount_rules: (it.discount_rules || []).map((r) => ({
            min_duration: String(r.min_duration), discount_pct: String(r.discount_pct),
          })),
          weekend_promo: it.weekend_promo && it.weekend_promo.active
            ? { active: true, type: it.weekend_promo.type || "DISCOUNT_PERCENT",
                discount_pct: it.weekend_promo.discount_pct != null ? String(it.weekend_promo.discount_pct) : "",
                pickup_from: it.weekend_promo.pickup_from || "12:00", return_until: it.weekend_promo.return_until || "12:00" }
            : { active: false, type: "DISCOUNT_PERCENT", discount_pct: "", pickup_from: "12:00", return_until: "12:00" },
          quantity_discount_rules: (it.quantity_discount_rules || []).map((r) => ({
            min_quantity: String(r.min_quantity), discount_pct: String(r.discount_pct),
          })),
          // V17: cadastro avançado
          qty_min: it.qty_rules?.min_qty > 1 ? String(it.qty_rules.min_qty) : "",
          qty_step: it.qty_rules?.step > 1 ? String(it.qty_rules.step) : "",
          conv_active: !!it.qty_rules?.conversion_active,
          conv_question: it.qty_rules?.conversion_question || "",
          conv_unit: it.qty_rules?.conversion_unit || "",
          conv_factor: it.qty_rules?.conversion_factor ? String(it.qty_rules.conversion_factor) : "",
          conv_required: !!it.qty_rules?.conversion_required,
          // V17.10.1 — grupos de opcionais (persistidos ao lado dos complements)
          addon_groups: Array.isArray(it.addon_groups) ? it.addon_groups : [],
          complements: (it.complements || []).map((c) => ({
            kind: c.kind, name: c.name || "", required: !!c.required, charge: c.charge || "per_rental",
            price: c.price != null ? String(c.price) : "", max_qty: String(c.max_qty || 1),
            image: c.image || "",
            // V17.5: estoque + pausa
            stock_qty: c.stock_qty != null ? String(c.stock_qty) : "",
            paused: !!c.paused,
            require_active: !!c.require_active,
            require_mode: c.require_mode || (c.require_active ? "proportional" : "optional"),
            require_min_fixed: c.require_min_fixed != null ? String(c.require_min_fixed) : "1",
            require_per_source: c.require_per_source || "quantity",
            require_per_base_qty: c.require_per_base_qty != null ? String(c.require_per_base_qty) : "2",
            require_ratio: c.require_ratio != null ? String(c.require_ratio) : "1",
            // V17.10 — grupos de opcionais (só usados quando kind==="addon")
            group_key: c.group_key || "",
            group_label: c.group_label || "",
            group_mode: c.group_mode || "",
            group_max: c.group_max != null ? Number(c.group_max) : 2,
            description: c.description || "",
            options: (c.options || []).map((o) => ({
              label: o.label || "",
              price_delta: o.price_delta != null ? String(o.price_delta) : "",
              image: o.image || "",
              is_default: !!o.is_default,
              description: o.description || "",
            })),
          })),
          caucao_active: !!it.caucao?.active,
          caucao_type: it.caucao?.type || "fixed",
          caucao_value: it.caucao?.value ? String(it.caucao.value) : "",
          caucao_accept: !!it.caucao?.accepted_card_only,
          combos: (it.combos || []).map((cb) => ({
            listing_id: cb.listing_id || "", discount_pct: cb.discount_pct != null ? String(cb.discount_pct) : "", note: cb.note || "",
          })),
        });
      });
    }
    // eslint-disable-next-line
  }, []);

  if (!user || user.status !== "ativo_verificado") {
    return (
      <div className="max-w-md mx-auto p-6 text-center" data-testid="new-listing-gate">
        <Camera size={48} weight="duotone" className="mx-auto text-[#FF6B35]" />
        <h2 className="font-heading font-black text-2xl mt-3">Conclua seu cadastro para anunciar</h2>
        <button onClick={() => setOpen(true)} className="btn-primary mt-3" data-testid="open-auth-from-newlisting">
          Completar agora
        </button>
      </div>
    );
  }

  // V9.0: exige chave PIX cadastrada e validada para anunciar (recebimento)
  if (pixValidated === false) {
    return (
      <div className="max-w-md mx-auto p-6 text-center" data-testid="new-listing-pix-gate">
        <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
          <Camera size={26} weight="duotone" className="text-emerald-600" />
        </div>
        <h2 className="font-heading font-black text-2xl mt-3">Cadastre sua chave PIX para anunciar</h2>
        <p className="text-slate-600 mt-2">
          Para publicar um anúncio e receber os pagamentos das suas locações, cadastre e valide uma chave PIX
          vinculada ao seu CPF/CNPJ. É rápido e seguro.
        </p>
        <Link to="/carteira" className="btn-primary mt-4 inline-flex" data-testid="register-pix-from-newlisting">
          Cadastrar chave PIX
        </Link>
      </div>
    );
  }

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const updateTask = (id, patch) => setUploadTasks((prev) => prev.map((t) => t.id === id ? { ...t, ...patch } : t));
  const removeTask = (id) => setUploadTasks((prev) => prev.filter((t) => t.id !== id));

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    await processFiles(files);
    // limpa o valor pra permitir re-selecionar o mesmo arquivo
    try { e.target.value = ""; } catch { /* ignore */ }
  };

  const processFiles = async (files) => {
    if (!files.length) return;
    setUploading(true);
    // Cria tasks pra cada arquivo (preview local + estado inicial)
    const tasks = files.map((file) => ({
      id: `up_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      name: file.name || "foto",
      previewUrl: file.type?.startsWith("image/") ? URL.createObjectURL(file) : null,
      phase: "compressing",
      progress: 0,
      file,
    }));
    setUploadTasks((prev) => [...prev, ...tasks]);
    const newImages = [];
    const newThumbs = [];
    for (const task of tasks) {
      try {
        updateTask(task.id, { phase: "compressing", progress: 5 });
        const { path, thumb } = await processAndUploadImagePair(task.file, {
          fullOptions: {
            onProgress: (p) => {
              // browser-image-compression também emite progress durante a compressão (0..100)
              updateTask(task.id, { phase: "compressing", progress: Math.min(35, Math.round(p * 0.35)) });
            },
          },
          onUploadProgress: (pct) => {
            // pct = 0..100 do upload de rede (imagem cheia)
            updateTask(task.id, { phase: "uploading", progress: 35 + Math.round(pct * 0.55) });
          },
          onThumbProgress: (pct) => {
            // últimos 10% dedicados à miniatura
            updateTask(task.id, { phase: "uploading", progress: 90 + Math.round(pct * 0.1) });
          },
        });
        updateTask(task.id, { phase: "done", progress: 100 });
        newImages.push(path);
        newThumbs.push(thumb || path);
        // remove task depois de 400ms pra dar sensação de "check verde"
        setTimeout(() => {
          try { if (task.previewUrl) URL.revokeObjectURL(task.previewUrl); } catch { /* ignore */ }
          removeTask(task.id);
        }, 400);
      } catch (err) {
        updateTask(task.id, { phase: "error", progress: 0, error: err.message || "Falha no upload" });
        toast.error(err.message || formatApiError(err.response?.data?.detail));
      }
    }
    setForm((f) => ({
      ...f,
      images: [...f.images, ...newImages],
      thumbnails: [...(f.thumbnails || []), ...newThumbs],
    }));
    setUploading(false);
  };

  const removeImage = (idx) => setForm((f) => ({
    ...f,
    images: f.images.filter((_, i) => i !== idx),
    thumbnails: (f.thumbnails || []).filter((_, i) => i !== idx),
  }));

  // V7.7: aceita múltiplos separadores (, ; - | enter) e adiciona em batch
  const SEPARATORS_RE = /[,;|]/;  // hífen pode aparecer em palavras compostas (ex: "porta-malas") — não usar como separador
  const addTagsFromString = (raw) => {
    if (!raw) return;
    const candidates = raw.split(SEPARATORS_RE).map((s) => s.trim().toLowerCase()).filter(Boolean);
    if (candidates.length === 0) return;
    const seen = new Set(form.tags);
    const next = [...form.tags];
    let blocked = false;
    for (const c of candidates) {
      if (seen.has(c)) continue;
      if (next.length >= 5) { blocked = true; break; }
      next.push(c);
      seen.add(c);
    }
    setForm({ ...form, tags: next });
    setTagInput("");
    if (blocked) toast.error("Máximo de 5 palavras-chave");
  };
  // Chamado ao apertar Enter ou clicar no botão "+"
  const addTag = () => addTagsFromString(tagInput);

  // Auto-split: enquanto digita, se identificar separador no final do texto, divide automaticamente
  const onTagInputChange = (e) => {
    const v = e.target.value;
    // Se contém separador, processa em batch (sem precisar do Enter)
    if (SEPARATORS_RE.test(v)) {
      addTagsFromString(v);
    } else {
      setTagInput(v);
    }
  };

  const removeTag = (t) => setForm({ ...form, tags: form.tags.filter((x) => x !== t) });

  const requiresFrete = form.regras_entrega === "eu_entrego" || form.regras_entrega === "ambos";

  // V17.10.2 — Helper unificado de erro: exibe toast, muda para o step do campo faltando
  // e rola até o elemento (via data-testid) com destaque temporário.
  const failValidation = (message, targetStep = null, testId = null) => {
    toast.error(message);
    if (targetStep && step !== targetStep) setStep(targetStep);
    if (testId) {
      // Espera o step ser aplicado antes de tentar rolar
      setTimeout(() => {
        try {
          const el = document.querySelector(`[data-testid="${testId}"]`);
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
            el.classList.add("ring-2", "ring-red-500", "ring-offset-2");
            setTimeout(() => el.classList.remove("ring-2", "ring-red-500", "ring-offset-2"), 2200);
            try { el.focus?.({ preventScroll: true }); } catch { /* ignore */ }
          }
        } catch { /* ignore */ }
      }, 120);
    }
  };

  const next = () => {
    if (step === 1 && form.images.length === 0) {
      return failValidation("Adicione pelo menos 1 foto do item para continuar.", 1, LISTING_FORM.uploadImageBtn);
    }
    if (step === 2) {
      if (!form.title?.trim()) return failValidation('Informe o "Título" do anúncio.', 2, LISTING_FORM.titleInput);
      if (form.categories.length === 0) return failValidation("Escolha ao menos 1 categoria para o anúncio.", 2, LISTING_FORM.categoriesBlock);
      if (!form.price_per_day) return failValidation('Informe o "Preço" do item (por dia ou por mês).', 2, LISTING_FORM.priceInput);
      if (parseFloat(form.price_per_day) < 5) return failValidation("O valor mínimo do anúncio é R$ 5,00.", 2, LISTING_FORM.priceInput);
      if (form.tags.length < 3) return failValidation("Adicione pelo menos 3 palavras-chave (ajuda a aparecer nas buscas).", 2, LISTING_FORM.tagInput);
    }
    setStep(step + 1);
  };

  const save = async () => {
    // Se estamos no passo 1 ou 2 e algum básico faltar, valida ANTES (evita chegar aqui com forms vazios)
    if (form.images.length === 0) return failValidation("Adicione pelo menos 1 foto do item.", 1, LISTING_FORM.uploadImageBtn);
    if (!form.title?.trim()) return failValidation('Informe o "Título" do anúncio.', 2, LISTING_FORM.titleInput);
    if (form.categories.length === 0) return failValidation("Escolha ao menos 1 categoria.", 2, LISTING_FORM.categoriesBlock);
    if (!form.price_per_day) return failValidation('Informe o "Preço" do anúncio.', 2, LISTING_FORM.priceInput);
    if (parseFloat(form.price_per_day) < 5) return failValidation("O valor mínimo do anúncio é R$ 5,00.", 2, LISTING_FORM.priceInput);
    if (form.tags.length < 3) return failValidation("Adicione pelo menos 3 palavras-chave.", 2, LISTING_FORM.tagInput);

    // Epic 2: monta e valida a config de frete
    let freteConfig = null;
    if (requiresFrete) {
      const t = form.frete_tipo || "fixo";
      if (t === "fixo" && (!form.frete_valor_fixo || parseFloat(form.frete_valor_fixo) <= 0)) {
        return failValidation("Informe o valor fixo do frete (> 0).", 3, "frete-valor-fixo");
      }
      if (t === "por_km" && (!form.frete_valor_por_km || parseFloat(form.frete_valor_por_km) <= 0)) {
        return failValidation("Informe o valor por km do frete (> 0).", 3, "frete-valor-por-km");
      }
      freteConfig = {
        tipo: t,
        raio_gratis_km: parseFloat(form.frete_raio_gratis_km || 0) || 0,
        valor_fixo: parseFloat(form.frete_valor_fixo || 0) || 0,
        valor_por_km: parseFloat(form.frete_valor_por_km || 0) || 0,
        ida_volta: !!form.frete_ida_volta,
      };
    }
    if (!form.address_id) return failValidation("Selecione um endereço de onde o item será retirado/entregue.", 3, "address-picker");
    // V17: validações do cadastro avançado
    const priceNum = parseFloat(form.price_per_day || 0) || 0;
    // V17.9.8 — Validação EXPLÍCITA de complementos ANTES do filtro/limpeza para não sumir com
    // variantes/opcionais/consumíveis silenciosamente.
    const rawComps = form.complements || [];
    for (let ci = 0; ci < rawComps.length; ci++) {
      const c = rawComps[ci];
      const kindLabel = c.kind === "variant" ? "variante" : c.kind === "addon" ? "opcional" : "consumível";
      if (!(c.name || "").trim()) {
        return failValidation(`Dê um nome à ${kindLabel} #${ci + 1} antes de publicar (ex.: "Cor", "Tamanho").`, 3, `adv-comp-name-${ci}`);
      }
      if (c.kind === "variant") {
        const validOpts = (c.options || []).filter((o) => (o.label || "").trim());
        if (validOpts.length < 2) {
          return failValidation(`A variante "${c.name.trim()}" precisa de pelo menos 2 opções com nome preenchido.`, 3, `adv-comp-name-${ci}`);
        }
      }
      if (c.kind !== "variant" && (c.price === "" || c.price == null)) {
        return failValidation(`Informe o preço do ${kindLabel} "${c.name.trim()}".`, 3, `adv-comp-price-${ci}`);
      }
    }
    const cleanComplements = rawComps
      .map((c) => ({
        kind: c.kind,
        name: c.name.trim(),
        required: !!c.required,
        charge: c.kind === "consumable" ? "per_rental" : (c.charge || "per_rental"),
        price: parseFloat(c.price || 0) || 0,
        max_qty: Math.max(1, parseInt(c.max_qty || 1, 10) || 1),
        image: c.kind === "variant" ? null : ((c.image || "").trim() || null),
        // V17.5: estoque próprio + pausa
        stock_qty: c.kind === "variant" ? null : (
          c.stock_qty === "" || c.stock_qty == null ? null : Math.max(0, parseInt(c.stock_qty, 10) || 0)
        ),
        paused: c.kind === "variant" ? false : !!c.paused,
        // V17.2+: obrigatoriedade — só faz sentido em addon; require_mode do backend só aceita
        // "fixed" ou "proportional". V17.9.8+: nunca enviamos "optional" (invalidava o payload
        // ao editar consumíveis/variantes) — para addon "optional" mandamos proportional + require_active=false.
        require_active: c.kind === "addon" && !!c.require_active && c.require_mode !== "optional",
        require_mode: (c.kind === "addon" && c.require_mode === "fixed") ? "fixed" : "proportional",
        require_min_fixed: Math.max(1, parseInt(c.require_min_fixed || 1, 10) || 1),
        require_per_source: c.require_per_source === "measure" ? "measure" : "quantity",
        require_per_base_qty: Math.max(1, parseInt(c.require_per_base_qty || 1, 10) || 1),
        require_ratio: Math.max(1, parseInt(c.require_ratio || 1, 10) || 1),
        // V17.10 — grupos de opcionais
        group_key: (c.group_key || "").trim() || null,
        group_label: (c.group_label || "").trim() || null,
        group_mode: c.group_key && (c.group_mode === "exclusive" || c.group_mode === "max") ? c.group_mode : null,
        group_max: c.group_key && c.group_mode === "max" ? Math.max(1, parseInt(c.group_max || 2, 10) || 2) : null,
        description: (c.description || "").trim() || null,
        options: c.kind === "variant"
          ? (() => {
              const opts = (c.options || []).filter((o) => (o.label || "").trim());
              if (!opts.length) return null;
              // Garantir exatamente UMA opção marcada como padrão (default para a primeira, se nenhuma foi marcada)
              const hasDefault = opts.some((o) => !!o.is_default);
              return opts.map((o, oi) => ({
                label: o.label.trim(),
                // Padrão SEMPRE tem delta=0 (é o próprio preço-base do anúncio). Demais nunca subtraem.
                price_delta: (o.is_default || (!hasDefault && oi === 0)) ? 0 : Math.max(0, parseFloat(o.price_delta || 0) || 0),
                image: (o.image || "").trim() || null,
                is_default: hasDefault ? !!o.is_default : oi === 0,
                description: (o.description || "").trim() || null,
              }));
            })()
          : null,
      }));
    for (const c of cleanComplements) {
      if (c.kind === "variant" && (c.options || []).length < 2) {
        return failValidation(`A variante "${c.name}" precisa de pelo menos 2 opções.`, 3);
      }
    }
    if (form.caucao_active) {
      const cv = parseFloat(form.caucao_value || 0) || 0;
      if (cv <= 0) return failValidation("Informe o valor da caução (deve ser maior que zero).", 3, "caucao-value");
      if (form.caucao_type === "percent" && cv > 100) return failValidation("A caução em % não pode passar de 100%.", 3, "caucao-value");
      if (form.caucao_type === "fixed" && priceNum > 0 && cv > priceNum * 10) {
        return failValidation(`A caução não pode exceder 10× o valor do anúncio (máx. ${formatBRL(priceNum * 10)}).`, 3, "caucao-value");
      }
      if (!form.caucao_accept) {
        return failValidation("Para exigir caução, aceite que este item receberá pagamento somente via cartão de crédito.", 3, "caucao-accept");
      }
    }
    // V17.10.2 — Validação dos combos: cada linha precisa de listing selecionado + desconto 0..90.
    const rawCombos = form.combos || [];
    for (let bi = 0; bi < rawCombos.length; bi++) {
      const cb = rawCombos[bi];
      if (!cb.listing_id) {
        return failValidation(`No combo #${bi + 1}, escolha qual anúncio será oferecido junto.`, 3, `adv-combo-listing-${bi}`);
      }
      const p = parseFloat(cb.discount_pct);
      if (cb.discount_pct !== "" && cb.discount_pct != null && (!Number.isFinite(p) || p < 0 || p > 90)) {
        return failValidation(`No combo #${bi + 1}, o desconto precisa ser um número entre 0 e 90%.`, 3, `adv-combo-discount-${bi}`);
      }
    }
    // Valida combos_bundle e progressive quando o modo exige
    if (form.combo_upsell_mode === "total_bundle") {
      const bp = parseFloat(form.combo_bundle_discount_pct);
      if (!Number.isFinite(bp) || bp <= 0 || bp > 90) {
        return failValidation("No modo Kit único, defina um desconto entre 1% e 90%.", 3, "adv-upsell-bundle-pct");
      }
    }
    if (form.combo_upsell_mode === "progressive") {
      const tiers = Array.isArray(form.combo_progressive) ? form.combo_progressive : [];
      if (tiers.length === 0) {
        return failValidation("No modo Escala progressiva, adicione pelo menos 1 faixa (ex.: 2 extras → 10%).", 3, "adv-upsell-progressive-block");
      }
      for (let ti = 0; ti < tiers.length; ti++) {
        const t = tiers[ti];
        const q = parseInt(t?.qty_extras || 0, 10);
        const d = parseFloat(t?.discount_pct);
        if (!q || q < 1 || q > 10) return failValidation(`Faixa #${ti + 1} do combo: quantidade de extras precisa ser entre 1 e 10.`, 3, `adv-upsell-tier-${ti}`);
        if (!Number.isFinite(d) || d < 0 || d > 90) return failValidation(`Faixa #${ti + 1} do combo: desconto precisa ser um número entre 0 e 90%.`, 3, `adv-upsell-tier-${ti}`);
      }
    }
    const cleanCombos = (form.combos || [])
      .filter((cb) => cb.listing_id)
      .map((cb) => ({ listing_id: cb.listing_id, discount_pct: parseFloat(cb.discount_pct || 0) || 0, note: (cb.note || "").trim() }));

    setSaving(true);
    try {
      const payload = {
        ...form,
        category: form.categories[0] || form.category,
        categories: form.categories,
        price_per_day: parseFloat(form.price_per_day),
        // Epic 2: frete dinâmico (valor_frete legado = valor fixo p/ compatibilidade)
        frete_config: freteConfig,
        valor_frete: requiresFrete ? (freteConfig?.tipo === "fixo" ? freteConfig.valor_fixo : 0) : 0,
        // V18.7 — Cutoffs por anúncio (opcional; se null usa config global do app)
        cutoff_retirada: (form.cutoff_retirada || "").trim() || null,
        cutoff_entrega: (form.cutoff_entrega || "").trim() || null,
        // V7.5: garante que quantidade_total sai como número >= 1 mesmo se ficou string vazia
        quantidade_total: Math.max(1, parseInt(form.quantidade_total || 1, 10) || 1),
        // Epic 1: modalidade de locação
        rental_mode: form.rental_mode || "legacy",
        cycle_unit: (form.rental_mode === "flexible" || form.rental_mode === "closed") ? form.cycle_unit : null,
        min_cycles: form.rental_mode === "flexible" ? Math.max(1, parseInt(form.min_cycles || 1, 10) || 1) : null,
        // V12.4: sanitiza regras de desconto/promoção; V18.9: sanitiza regras por quantidade
        ...sanitizePricingRules({ discount_rules: form.discount_rules, weekend_promo: form.weekend_promo, quantity_discount_rules: form.quantity_discount_rules }),
        // V17: cadastro avançado
        qty_rules: {
          min_qty: Math.max(1, parseInt(form.qty_min || 1, 10) || 1),
          step: Math.max(1, parseInt(form.qty_step || 1, 10) || 1),
          conversion_active: !!form.conv_active,
          conversion_question: (form.conv_question || "").trim(),
          conversion_unit: (form.conv_unit || "").trim(),
          conversion_factor: parseFloat(form.conv_factor || 1) || 1,
          conversion_required: !!form.conv_required,
        },
        complements: cleanComplements,
        // V17.10.1 — grupos de opcionais (persiste para o dono criar antes de assinalar addons)
        addon_groups: Array.isArray(form.addon_groups) ? form.addon_groups.filter((g) => g && (g.key || "").trim()) : [],
        caucao: {
          active: !!form.caucao_active,
          type: form.caucao_type || "fixed",
          value: parseFloat(form.caucao_value || 0) || 0,
          accepted_card_only: !!form.caucao_accept,
        },
        combos: cleanCombos,
        // V17.10.2 — normaliza campos numéricos do combo (evita 422 "unable to parse string as a number")
        combo_upsell_mode: form.combo_upsell_mode || "per_item",
        combo_stack_percentages: !!form.combo_stack_percentages,
        combo_bundle_discount_pct: Number.isFinite(parseFloat(form.combo_bundle_discount_pct)) ? Math.max(0, Math.min(90, parseFloat(form.combo_bundle_discount_pct))) : 0,
        combo_progressive: Array.isArray(form.combo_progressive)
          ? form.combo_progressive
              .map((t) => ({
                qty_extras: Math.max(1, Math.min(10, parseInt(t?.qty_extras || 1, 10) || 1)),
                discount_pct: Number.isFinite(parseFloat(t?.discount_pct)) ? Math.max(0, Math.min(90, parseFloat(t?.discount_pct))) : 0,
              }))
              .filter((t) => t.qty_extras >= 1)
          : [],
      };
      let data;
      if (isEdit) {
        const r = await api.patch(`/listings/${id}`, payload); data = r.data;
        toast.success("Anúncio atualizado!");
      } else {
        const r = await api.post("/listings", payload); data = r.data;
        toast.success("Anúncio publicado!");
        sessionStorage.removeItem(DRAFT_KEY);
      }
      // V17.9.7 — reset de scroll antes da navegação evita o "bounce" percebido no iOS
      // quando a próxima página é curta e o viewport ficou preso no fim do formulário.
      try { window.scrollTo({ top: 0, left: 0, behavior: "instant" }); } catch { /* ignore */ }
      nav(`/itens/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-5" data-testid="new-listing-page">
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => step > 1 ? setStep(step - 1) : nav(-1)} className="flex items-center gap-1 text-sm text-slate-500" data-testid="back-step">
          <ArrowLeft size={16} weight="bold" /> {step > 1 ? "Voltar" : "Voltar"}
        </button>
        <span className="aj-badge">Passo {step}/3 {isEdit && "(Edição)"}</span>
        {/* V17.9.4 — Simular disponível também no cadastro (usa preview via sessionStorage) */}
        <button
          type="button"
          onClick={() => {
            // Constrói um objeto "listing-like" a partir do form para o simulador
            const previewObj = {
              id: "preview",
              title: (form.title || "").trim() || "Anúncio em cadastro",
              description: form.description || "",
              price_per_day: parseFloat(form.price_per_day) || 0,
              tipo_cobranca: form.tipo_cobranca || "DIARIO",
              rental_mode: form.rental_mode || "legacy",
              cycle_unit: form.cycle_unit || "day",
              min_cycles: parseInt(form.min_cycles || 1, 10),
              regras_entrega: form.regras_entrega || "eu_entrego",
              frete_gratis: !!form.frete_gratis,
              raio_entrega_km: parseFloat(form.raio_entrega_km) || 0,
              taxa_por_km: parseFloat(form.taxa_por_km) || 0,
              taxa_minima_entrega: parseFloat(form.taxa_minima_entrega) || 0,
              quantidade_total: parseInt(form.quantidade_total || 1, 10),
              images: form.images || [],
              thumbnails: form.thumbnails || form.images || [],
              caucao_active: !!form.caucao_active,
              caucao_type: form.caucao_type || "fixed",
              caucao_value: parseFloat(form.caucao_value) || 0,
              caucao_amount: (() => {
                const v = parseFloat(form.caucao_value) || 0;
                if (!form.caucao_active) return 0;
                if (form.caucao_type === "percent") return 0; // depende de valor_diarias — recalc no front
                return v;
              })(),
              complements: form.complements || [],
              combos: form.combos || [],
              combo_upsell_mode: form.combo_upsell_mode || "per_item",
              combo_stack_percentages: !!form.combo_stack_percentages,
              combo_bundle_discount_pct: parseFloat(form.combo_bundle_discount_pct) || 0,
              combo_progressive: form.combo_progressive || [],
              conv_active: !!form.conv_active,
              conv_question: form.conv_question || "",
              conv_unit: form.conv_unit || "",
              conv_factor: parseFloat(form.conv_factor) || 0,
              conv_required: !!form.conv_required,
              qty_min: parseInt(form.qty_min || 1, 10),
              qty_step: parseInt(form.qty_step || 1, 10),
              owner_id: user?.id,
              owner_name: user?.name || "",
              is_active: true,
              // Preview flags
              _is_preview: true,
              combos_expanded: [],  // sem enrichment durante preview (não temos backend)
            };
            try { sessionStorage.setItem("aj_listing_preview", JSON.stringify(previewObj)); } catch { /* ignore */ }
            nav(`/itens/preview?simulate=1`);
          }}
          className="text-xs px-3 py-1.5 rounded-full border-2 border-amber-200 text-amber-700 hover:bg-amber-50 inline-flex items-center gap-1 font-bold"
          data-testid="simulate-btn-form"
          title="Ver como o cliente vê"
        >
          🧪 Simular
        </button>
        {!isEdit && (
          <button
            onClick={() => setShowDiscardConfirm(true)}
            className="text-xs px-3 py-1.5 rounded-full border-2 border-red-200 text-red-600 hover:bg-red-50 inline-flex items-center gap-1 font-bold"
            data-testid="discard-draft-btn"
            title="Descartar tudo e sair"
          >
            <X size={12} weight="bold" /> Descartar
          </button>
        )}
      </div>
      <ConfirmDialog
        open={showDiscardConfirm}
        onClose={() => setShowDiscardConfirm(false)}
        onConfirm={() => {
          setShowDiscardConfirm(false);
          try { sessionStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
          toast.success("Rascunho descartado");
          nav("/meus-anuncios");
        }}
        title="Descartar rascunho?"
        description="Todas as informações preenchidas até agora serão perdidas. Essa ação não pode ser desfeita."
        confirmLabel="Descartar"
        variant="danger"
        testid="confirm-discard-draft"
      />
      <div className="flex gap-1.5 mb-5">
        {[1, 2, 3].map((n) => (
          <div key={n} className={`h-1.5 flex-1 rounded-full ${n <= step ? "bg-[#FF6B35]" : "bg-slate-200"}`} />
        ))}
      </div>

      {step > 1 && (
        <ListingMiniSummary image={form.thumbnails?.[0] || form.images?.[0]} title={form.title} />
      )}

      {step === 1 && (
        <div className="space-y-4" data-testid="new-listing-step-1">
          <div>
            <h2 className="font-heading font-black text-2xl">Fotos</h2>
            <p className="text-sm text-slate-600">Adicione 3 ou mais para chamar atenção.</p>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {form.images.map((p, idx) => (
              <div key={p} className="relative aspect-square rounded-xl overflow-hidden border border-slate-200">
                <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => removeImage(idx)} className="absolute top-1 right-1 bg-white/95 rounded-full p-1" data-testid="remove-image-btn">
                  <Trash size={14} weight="bold" />
                </button>
              </div>
            ))}
            {/* V18.1 — Placeholder com progress circular por foto sendo enviada */}
            {uploadTasks.map((t) => (
              <UploadProgressTile key={t.id} task={t} onRetry={() => processFiles([t.file])} onDismiss={() => removeTask(t.id)} />
            ))}
            <UploadImageButton onChange={onFiles} onCaptureMultiple={(files) => processFiles(files)} uploading={uploading} remaining={Math.max(1, 10 - form.images.length)} />
          </div>
          <button onClick={next} className="btn-primary w-full" data-testid="step1-next">
            Continuar <ArrowRight size={16} weight="bold" />
          </button>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-3" data-testid="new-listing-step-2">
          <div>
            <h2 className="font-heading font-black text-2xl">Detalhes</h2>
            <p className="text-sm text-slate-600">Título, categoria, preço e palavras-chave.</p>
          </div>
          <input placeholder="Título (ex: Furadeira Bosch GSB 13RE)" value={form.title} onChange={upd("title")} className="aj-input" data-testid={LISTING_FORM.titleInput} />
          <textarea placeholder="Descrição (estado, voltagem, acessórios)" rows={3} value={form.description} onChange={upd("description")} className="aj-input" data-testid="listing-description-input" />
          <div data-testid={LISTING_FORM.categoriesBlock}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] uppercase font-bold tracking-wider text-slate-500">Categorias (até 2)</span>
              <span className="text-[10px] text-slate-400">{form.categories.length}/2 selecionada(s)</span>
            </div>
            {form.categories.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2" data-testid="listing-selected-categories">
                {form.categories.map((c) => (
                  <button key={c} type="button" onClick={() => setForm((f) => ({ ...f, categories: f.categories.filter((x) => x !== c) }))}
                    className="text-xs font-bold bg-[#004E98] text-white rounded-full px-2.5 py-1 inline-flex items-center gap-1" data-testid={`selected-cat-${c}`}>
                    {c} <span className="font-black">×</span>
                  </button>
                ))}
              </div>
            )}
            <input
              value={catSearch} onChange={(e) => setCatSearch(e.target.value)}
              placeholder="Buscar categoria..." className="aj-input" data-testid="listing-category-search"
            />
            <div className="max-h-44 overflow-y-auto mt-2 rounded-xl border border-slate-200 divide-y divide-slate-100" data-testid="listing-category-options">
              {categoriesAll
                .filter((c) => c.toLowerCase().includes(catSearch.trim().toLowerCase()))
                .map((c) => {
                  const sel = form.categories.includes(c);
                  const full = form.categories.length >= 2 && !sel;
                  return (
                    <button key={c} type="button" disabled={full}
                      onClick={() => setForm((f) => ({ ...f, categories: sel ? f.categories.filter((x) => x !== c) : [...f.categories, c] }))}
                      className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between ${sel ? "bg-[#004E98]/5 font-bold text-[#004E98]" : full ? "text-slate-300 cursor-not-allowed" : "hover:bg-slate-50"}`}
                      data-testid={`cat-option-${c}`}>
                      {c}{sel && <span>✓</span>}
                    </button>
                  );
                })}
              {categoriesAll.filter((c) => c.toLowerCase().includes(catSearch.trim().toLowerCase())).length === 0 && (
                <div className="px-3 py-3 text-xs text-slate-400">Nenhuma categoria encontrada.</div>
              )}
            </div>
          </div>
          <label className="block">
            <span className="text-[11px] uppercase font-bold tracking-wider text-slate-500">
              {(form.rental_mode === "flexible" || form.rental_mode === "closed")
                ? `Valor por ${({ day: "dia", week: "semana", fortnight: "quinzena", month: "mês" }[form.cycle_unit] || "ciclo")} (R$)`
                : (form.tipo_cobranca === "MENSAL" ? "Valor MENSAL (R$)" : "Diária (R$)")}
            </span>
            <input type="number" step="0.01" min="5" placeholder="50.00" value={form.price_per_day} onChange={upd("price_per_day")} className="aj-input mt-1" data-testid={LISTING_FORM.priceInput} />
            <span className="text-[10px] text-slate-400 block mt-0.5">Valor mínimo: R$ 5,00.</span>
          </label>

          {/* Epic 1: Modalidade de locação */}
          <div>
            <span className="text-[11px] uppercase font-bold tracking-wider text-slate-500">Modalidade de locação</span>
            {/* Padrão sempre visível */}
            <button
              type="button"
              onClick={() => setForm({ ...form, rental_mode: "legacy" })}
              className={`text-left w-full p-3 rounded-xl border-2 mt-1 ${form.rental_mode === "legacy" ? "border-[#004E98] bg-[#004E98]/5" : "border-slate-200"}`}
              data-testid="rental-mode-legacy"
            >
              <div className="font-bold text-sm">Padrão</div>
              <div className="text-[10px] text-slate-500 mt-0.5">Aluguel por diária ou mensal, sem regras de ciclo.</div>
            </button>

            {/* V17.9 — Modalidades avançadas (Mínimo flexível + Prazo fechado) em colapsável */}
            <button
              type="button"
              onClick={() => setShowAdvancedRentalModes((v) => !v)}
              className="w-full flex items-center justify-between mt-2 px-3 py-2.5 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 transition"
              data-testid="advanced-rental-modes-toggle"
            >
              <span className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                🧩 Modalidades avançadas <span className="text-[10px] font-normal text-slate-500">(mínimo flexível, prazo fechado)</span>
                {(form.rental_mode === "flexible" || form.rental_mode === "closed") && (
                  <span className="text-[9px] font-black text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5 ml-1">
                    {form.rental_mode === "flexible" ? "Mín. flexível" : "Prazo fechado"} ativo
                  </span>
                )}
              </span>
              <CaretDown size={14} weight="bold" className={`text-slate-400 transition-transform duration-200 ${showAdvancedRentalModes ? "rotate-180" : ""}`} />
            </button>

            {showAdvancedRentalModes && (
              <div className="mt-2 rounded-xl border-2 border-slate-200 bg-slate-50/60 p-2.5 space-y-2.5" data-testid="advanced-rental-modes-body">
                <div className="grid grid-cols-1 gap-2">
                  {[
                    { v: "flexible", label: "Mínimo flexível", hint: "Exige um tempo mínimo; pode estender dia a dia (pro-rata)." },
                    { v: "closed", label: "Prazo fechado (ciclos)", hint: "Só em blocos fechados (ex: 30/60/90 dias)." },
                  ].map((o) => (
                    <button
                      key={o.v}
                      type="button"
                      onClick={() => {
                        const next = { ...form, rental_mode: o.v };
                        if (o.v !== "legacy" && !form.cycle_unit) next.cycle_unit = form.tipo_cobranca === "MENSAL" ? "month" : "day";
                        setForm(next);
                      }}
                      className={`text-left p-3 rounded-xl border-2 bg-white ${form.rental_mode === o.v ? "border-[#004E98]" : "border-slate-200"}`}
                      data-testid={`rental-mode-${o.v}`}
                    >
                      <div className="font-bold text-sm">{o.label}</div>
                      <div className="text-[10px] text-slate-500 mt-0.5">{o.hint}</div>
                    </button>
                  ))}
                </div>

                {(form.rental_mode === "flexible" || form.rental_mode === "closed") && (
                  <div className="space-y-3 rounded-xl border-2 border-[#004E98]/20 bg-[#004E98]/[0.03] p-3" data-testid="cycle-config-block">
                    <div>
                      <span className="text-[11px] uppercase font-bold tracking-wider text-slate-500">Ciclo de locação</span>
                      <div className="grid grid-cols-2 gap-2 mt-1">
                        {[
                          { v: "day", label: "Diário", days: 1 },
                          { v: "week", label: "Semanal", days: 7 },
                          { v: "fortnight", label: "Quinzenal", days: 15 },
                          { v: "month", label: "Mensal", days: 30 },
                        ].map((o) => (
                          <button
                            key={o.v}
                            type="button"
                            onClick={() => setForm({ ...form, cycle_unit: o.v, tipo_cobranca: o.v === "day" ? "DIARIO" : "MENSAL" })}
                            className={`text-left p-2.5 rounded-lg border-2 ${form.cycle_unit === o.v ? "border-[#004E98] bg-white" : "border-slate-200 bg-white/60"}`}
                            data-testid={`cycle-unit-${o.v}`}
                          >
                            <div className="font-bold text-xs">{o.label}</div>
                            <div className="text-[10px] text-slate-500">{o.days} dia(s)</div>
                          </button>
                        ))}
                      </div>
                    </div>
                    {form.rental_mode === "flexible" && (
                      <label className="block">
                        <span className="text-[11px] uppercase font-bold tracking-wider text-slate-500">
                          Tempo mínimo (em ciclos de {({ day: "1 dia", week: "1 semana", fortnight: "1 quinzena", month: "1 mês" }[form.cycle_unit] || "ciclo")})
                        </span>
                        <input
                          type="number" min="1" step="1"
                          value={form.min_cycles}
                          onChange={(e) => { const n = parseInt(e.target.value, 10); setForm({ ...form, min_cycles: isNaN(n) ? "" : Math.max(1, n) }); }}
                          onBlur={() => { if (!form.min_cycles || form.min_cycles < 1) setForm({ ...form, min_cycles: 1 }); }}
                          className="aj-input mt-1" data-testid="min-cycles-input"
                        />
                        <span className="text-[10px] text-slate-400 block mt-0.5">
                          Mínimo = {(parseInt(form.min_cycles || 1, 10) || 1) * ({ day: 1, week: 7, fortnight: 15, month: 30 }[form.cycle_unit] || 1)} dia(s). Depois disso o cliente estende dia a dia.
                        </span>
                      </label>
                    )}
                    {form.rental_mode === "closed" && (
                      <div className="text-[11px] text-slate-600 bg-white rounded-lg p-2 border border-slate-200">
                        O cliente escolhe <b>quantos blocos</b> de {({ day: 1, week: 7, fortnight: 15, month: 30 }[form.cycle_unit] || 1)} dia(s). O calendário trava a data-fim automaticamente.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* V7.2: Tipo de cobrança DIARIO / MENSAL (apenas no modo Padrão) */}
          {form.rental_mode === "legacy" && (
          <div>
            <span className="text-[11px] uppercase font-bold tracking-wider text-slate-500">Tipo de cobrança</span>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {[
                { v: "DIARIO", label: "Por dia", hint: "Ferramentas, máquinas e itens de uso curto" },
                { v: "MENSAL", label: "Por mês", hint: "Containers, andaimes, equipamentos de obra" },
              ].map((o) => (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => setForm({ ...form, tipo_cobranca: o.v })}
                  className={`text-left p-3 rounded-xl border-2 ${form.tipo_cobranca === o.v ? "border-[#004E98] bg-[#004E98]/5" : "border-slate-200"}`}
                  data-testid={`tipo-cobranca-${o.v}`}
                >
                  <div className="font-bold text-sm">{o.label}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">{o.hint}</div>
                </button>
              ))}
            </div>
          </div>
          )}

          {/* V7.2: Estoque (quantidade total disponível) */}
          <label className="block">
            <span className="text-[11px] uppercase font-bold tracking-wider text-slate-500">Estoque (unidades disponíveis)</span>
            <input
              type="number"
              min="1"
              step="1"
              placeholder="1"
              value={form.quantidade_total === "" ? "" : form.quantidade_total}
              onChange={(e) => {
                const v = e.target.value;
                // Permite campo vazio durante edição (fix: antes forçava "1" e ficava "18" ao digitar "8")
                if (v === "") { setForm({ ...form, quantidade_total: "" }); return; }
                const n = parseInt(v, 10);
                if (!isNaN(n)) setForm({ ...form, quantidade_total: Math.max(1, n) });
              }}
              onBlur={() => {
                // Normaliza ao sair do campo — se vazio, volta a 1
                if (form.quantidade_total === "" || form.quantidade_total < 1) {
                  setForm({ ...form, quantidade_total: 1 });
                }
              }}
              className="aj-input mt-1"
              data-testid="listing-quantidade-input"
            />
            <span className="text-[10px] text-slate-400 block mt-0.5">
              Quantas unidades você tem? Ex: 100 cadeiras de festa. Para item único, deixe em 1.
            </span>
          </label>

          {parseFloat(form.price_per_day || 0) > 0 && (() => {
            const usingCycle = form.rental_mode === "flexible" || form.rental_mode === "closed";
            const cycleWord = { day: "dia", week: "semana", fortnight: "quinzena", month: "mês" }[form.cycle_unit] || "ciclo";
            const isMonthly = form.tipo_cobranca === "MENSAL";
            const unitLabel = usingCycle ? cycleWord : (isMonthly ? "mês" : "dia");
            const priceLabel = usingCycle ? `Você definiu o valor por ${cycleWord} em:` : (isMonthly ? "Você definiu o valor MENSAL em:" : "Você definiu a DIÁRIA em:");
            const price = parseFloat(form.price_per_day);
            const FLOOR = 2.49;            // piso: Asaas Pix (R$1,99) + margem app (R$0,50)
            const pctCommission = price * 0.10;
            const commission = Math.max(pctCommission, FLOOR);
            const isFloor = pctCommission < FLOOR;
            const net = Math.max(0, price - commission);
            return (
            <div className="aj-card p-3 bg-gradient-to-br from-emerald-50 to-emerald-100/50 border border-emerald-200" data-testid="earnings-calculator">
              <div className="text-[10px] uppercase font-bold tracking-wider text-emerald-700 mb-1">Resumo de Ganhos {isMonthly ? "(MENSAL)" : "(DIÁRIO)"}</div>
              <div className="space-y-0.5 text-xs">
                <div className="flex justify-between">
                  <span className="text-slate-600">{priceLabel}</span>
                  <span className="font-semibold" data-testid="earnings-price">{formatBRL(price)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">{isFloor ? "Taxa mínima de intermediação" : "Taxa de Intermediação AlugaJá (10%)"}</span>
                  <span className="font-semibold text-red-600" data-testid="earnings-commission">- {formatBRL(commission)}</span>
                </div>
                <div className="flex justify-between font-heading font-black text-sm pt-1 border-t border-emerald-200 mt-1">
                  <span>Você recebe líquido:</span>
                  <span className="text-emerald-700" data-testid="earnings-net">
                    {formatBRL(net)} <span className="text-[10px] font-normal text-slate-500">/ {unitLabel}</span>
                  </span>
                </div>
              </div>
              {isFloor && (
                <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-1.5 mt-2" data-testid="earnings-floor-note">
                  Para cobrir os custos de processamento bancário de anúncios pequenos, a taxa mínima de intermediação é de {formatBRL(FLOOR)}.
                </div>
              )}
              <a href="/termos" className="block text-[10px] text-emerald-700 hover:underline mt-2" data-testid="terms-link">
                Ler Termos e Políticas de Pagamento →
              </a>
            </div>
            );
          })()}

          {/* Epic 3: Descontos progressivos (por diárias OU meses/ciclos). Fim de semana só no diário. */}
          {(() => {
            const usingCycle = form.rental_mode === "flexible" || form.rental_mode === "closed";
            const cyclePlural = { day: "dias", week: "semanas", fortnight: "quinzenas", month: "meses" };
            const discUnit = usingCycle ? (cyclePlural[form.cycle_unit] || "ciclos") : (form.tipo_cobranca === "MENSAL" ? "meses" : "dias");
            const showWeekend = !usingCycle && form.tipo_cobranca !== "MENSAL";
            return (
              <PricingRulesEditor
                value={{ discount_rules: form.discount_rules, weekend_promo: form.weekend_promo, quantity_discount_rules: form.quantity_discount_rules }}
                onChange={(v) => setForm({ ...form, discount_rules: v.discount_rules, weekend_promo: v.weekend_promo, quantity_discount_rules: v.quantity_discount_rules })}
                unitLabel={discUnit}
                showWeekend={showWeekend}
              />
            );
          })()}

          {/* V17: Configurações avançadas — quantidade, complementos, caução, combos */}
          {(() => {
            const usingCycle = form.rental_mode === "flexible" || form.rental_mode === "closed";
            const cycleWord = { day: "dia", week: "semana", fortnight: "quinzena", month: "mês" }[form.cycle_unit] || "ciclo";
            const unitWord = usingCycle ? cycleWord : (form.tipo_cobranca === "MENSAL" ? "mês" : "dia");
            return (
              <AdvancedListingConfig form={form} setForm={setForm} ownListings={ownListings} unitWord={unitWord} />
            );
          })()}

          <div>
            <span className="text-[11px] uppercase font-bold tracking-wider text-slate-500">Palavras-chave (3 a 5)</span>
            <div className="flex gap-2 mt-1">
              <div className="flex-1">
                <input
                  value={tagInput}
                  onChange={onTagInputChange}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                  placeholder="ex: furadeira, bosch, elétrica"
                  className="aj-input"
                  data-testid={LISTING_FORM.tagInput}
                  inputMode="text"
                  autoCapitalize="none"
                />
              </div>
              <button type="button" onClick={addTag} className="btn-blue px-3" data-testid="add-tag-btn" aria-label="Adicionar palavra-chave">
                <Plus size={16} weight="bold" />
              </button>
            </div>
            {form.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="tag-chips">
                {form.tags.map((t) => (
                  <span key={t} className="inline-flex items-center gap-1 bg-[#FF6B35]/10 text-[#FF6B35] px-2 py-0.5 rounded-full text-xs font-bold">
                    <Tag size={10} weight="bold" />
                    {t}
                    <button type="button" onClick={() => removeTag(t)} data-testid={`remove-tag-${t}`}><X size={10} weight="bold" /></button>
                  </span>
                ))}
              </div>
            )}
            <span className="text-[11px] text-slate-500 mt-1 block">
              {form.tags.length}/5 — separe por vírgula, ponto-e-vírgula ou enter
            </span>
          </div>

          <button onClick={next} className="btn-primary w-full" data-testid="step2-next">
            Continuar <ArrowRight size={16} weight="bold" />
          </button>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-3" data-testid="new-listing-step-3">
          <div>
            <h2 className="font-heading font-black text-2xl">Entrega & Endereço</h2>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {ENTREGA_OPTIONS.map(({ val, label, icon: Icon }) => (
              <button
                key={val}
                onClick={() => setForm({ ...form, regras_entrega: val })}
                className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1 text-xs font-bold ${form.regras_entrega === val ? "border-[#FF6B35] bg-[#FF6B35]/5" : "border-slate-200"}`}
                data-testid={`entrega-${val}`}
              >
                <Icon size={22} weight="bold" />
                {label}
              </button>
            ))}
          </div>

          {requiresFrete && (
            <div className="space-y-3 rounded-xl border-2 border-[#FF6B35]/20 bg-[#FF6B35]/[0.03] p-3" data-testid="frete-block">
              <span className="text-[11px] uppercase font-bold tracking-wider text-slate-500">Frete (entrega pelo dono)</span>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { v: "gratis", label: "Grátis" },
                  { v: "fixo", label: "Valor fixo" },
                  { v: "por_km", label: "Por km" },
                  { v: "combinar", label: "A combinar" },
                ].map((o) => (
                  <button
                    key={o.v}
                    type="button"
                    onClick={() => setForm({ ...form, frete_tipo: o.v })}
                    className={`p-2.5 rounded-lg border-2 text-xs font-bold ${form.frete_tipo === o.v ? "border-[#FF6B35] bg-white" : "border-slate-200 bg-white/60"}`}
                    data-testid={`frete-tipo-${o.v}`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>

              {form.frete_tipo === "fixo" && (
                <label className="block">
                  <span className="text-[11px] uppercase font-bold tracking-wider text-slate-500">Valor fixo (R$) <span className="text-[#FF6B35]">*</span></span>
                  <input type="number" step="0.01" min="0.01" placeholder="30.00" value={form.frete_valor_fixo} onChange={upd("frete_valor_fixo")} className="aj-input mt-1" data-testid="frete-valor-fixo" />
                </label>
              )}

              {form.frete_tipo === "por_km" && (
                <>
                  <label className="block">
                    <span className="text-[11px] uppercase font-bold tracking-wider text-slate-500">Valor por km (R$) <span className="text-[#FF6B35]">*</span></span>
                    <input type="number" step="0.01" min="0.01" placeholder="2.00" value={form.frete_valor_por_km} onChange={upd("frete_valor_por_km")} className="aj-input mt-1" data-testid="frete-valor-por-km" />
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.frete_ida_volta} onChange={(e) => setForm({ ...form, frete_ida_volta: e.target.checked })} className="w-4 h-4 accent-[#FF6B35]" data-testid="frete-ida-volta" />
                    <span className="text-xs font-bold text-slate-600">Cobrar ida e volta (2×)</span>
                  </label>
                </>
              )}

              {(form.frete_tipo === "fixo" || form.frete_tipo === "por_km") && (
                <label className="block">
                  <span className="text-[11px] uppercase font-bold tracking-wider text-slate-500">Raio de frete grátis (km)</span>
                  <input type="number" step="0.1" min="0" placeholder="0 (opcional)" value={form.frete_raio_gratis_km} onChange={upd("frete_raio_gratis_km")} className="aj-input mt-1" data-testid="frete-raio-gratis" />
                  <span className="text-[10px] text-slate-400 block mt-0.5">Entregas dentro deste raio saem de graça.</span>
                </label>
              )}

              {form.frete_tipo === "combinar" && (
                <div className="text-[11px] text-slate-600 bg-white rounded-lg p-2 border border-slate-200">
                  O frete aparece como <b>"a combinar"</b> no checkout (R$ 0,00). Você e o locatário acertam a entrega por fora do app.
                </div>
              )}
              {form.frete_tipo === "gratis" && (
                <div className="text-[11px] text-emerald-700 bg-white rounded-lg p-2 border border-emerald-200">
                  Entrega sempre <b>grátis</b> para o locatário.
                </div>
              )}
            </div>
          )}

          {/* V10.9: horários de atendimento agora ficam no PERFIL (valem p/ todos os anúncios) */}
          <div className="aj-card p-3 bg-blue-50 border-blue-200 flex items-start gap-2" data-testid="schedule-moved-note">
            <Clock size={16} weight="bold" className="text-[#004E98] mt-0.5 shrink-0" />
            <div className="text-[11px] text-slate-600">
              Seus <b>horários de atendimento</b> (dias e horas de entrega/retirada) agora ficam no seu{" "}
              <Link to="/perfil/editar" className="text-[#004E98] font-bold underline">Perfil</Link> e valem para todos os seus anúncios.
              O item controla apenas as <b>datas ocupadas</b>.
            </div>
          </div>

          {/* V18.7 — Cutoffs por anúncio (opcional) */}
          <div className="aj-card p-3 space-y-2" data-testid="cutoffs-config">
            <div className="text-xs font-black text-slate-800 flex items-center gap-1.5">
              <Clock size={14} weight="bold" className="text-[#004E98]" /> Horário limite no mesmo dia
              <span className="text-[10px] font-normal text-slate-400">(opcional)</span>
            </div>
            <p className="text-[11px] text-slate-500">
              Define até que hora do dia o cliente pode escolher retirar/receber HOJE mesmo. Deixe em branco para usar o padrão da plataforma.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <label className="block">
                <span className="text-[10px] uppercase font-bold text-slate-500">Retirada (padrão 17:00)</span>
                <input
                  type="time"
                  value={form.cutoff_retirada || ""}
                  onChange={(e) => setForm({ ...form, cutoff_retirada: e.target.value })}
                  placeholder="17:00"
                  className="aj-input mt-1"
                  data-testid="cutoff-retirada-input"
                />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase font-bold text-slate-500">Entrega (padrão 14:00)</span>
                <input
                  type="time"
                  value={form.cutoff_entrega || ""}
                  onChange={(e) => setForm({ ...form, cutoff_entrega: e.target.value })}
                  placeholder="14:00"
                  className="aj-input mt-1"
                  data-testid="cutoff-entrega-input"
                />
              </label>
            </div>
            <div className="text-[10px] text-slate-500 leading-relaxed">
              Ex.: se o item precisa de preparo entre locações, defina um horário mais cedo (ex.: 15:00 para retirada) — assim o cliente não escolhe pegar às 16:30 no mesmo dia.
            </div>
          </div>

          <div data-testid="address-picker">
            <span className="text-[11px] uppercase font-bold tracking-wider text-slate-500">Endereço de retirada</span>
            {addresses.length === 0 ? (
              <div className="mt-1 aj-card p-4 text-center" data-testid="no-addresses">
                <MapPin size={28} weight="duotone" className="mx-auto text-slate-300" />
                <p className="text-sm text-slate-500 mt-2">Você ainda não tem endereços cadastrados.</p>
                <Link
                  to={`/enderecos?returnTo=${encodeURIComponent(routerLocation.pathname)}`}
                  className="btn-blue mt-3 inline-flex text-sm py-2 px-4"
                  data-testid="link-add-address"
                >
                  Cadastrar endereço
                </Link>
              </div>
            ) : (
              <div className="mt-1 space-y-2">
                {addresses.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => setForm({ ...form, address_id: a.id })}
                    className={`w-full text-left p-3 rounded-xl border-2 ${form.address_id === a.id ? "border-[#FF6B35] bg-[#FF6B35]/5" : "border-slate-200"}`}
                    data-testid={`select-address-${a.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <MapPin size={16} weight="bold" className="text-[#004E98]" />
                      <span className="font-bold text-sm">{a.label}</span>
                      {a.is_default && <span className="aj-badge text-[10px]">Padrão</span>}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {a.street}, {a.number} · {a.neighborhood} · {a.city} - {a.uf}
                    </div>
                  </button>
                ))}
                <Link
                  to={`/enderecos?returnTo=${encodeURIComponent(routerLocation.pathname)}`}
                  className="text-sm text-[#004E98] font-bold inline-flex items-center gap-1"
                  data-testid="manage-addresses"
                >
                  <Plus size={14} weight="bold" /> Gerenciar endereços
                </Link>
              </div>
            )}
          </div>

          <button onClick={save} disabled={saving} className="btn-primary w-full" data-testid="save-listing-btn">
            <CheckCircle size={16} weight="bold" />
            {saving ? (isEdit ? "Salvando..." : "Publicando...") : (isEdit ? "Salvar alterações" : "Publicar Agora")}
          </button>
        </div>
      )}
    </div>
  );
}
